import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Cookie, Info, Settings, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CookiePolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card/50 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Cookie className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Cookie Policy</h1>
              <p className="text-sm text-muted-foreground">Last updated: October 20, 2025</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Introduction */}
        <section className="space-y-4">
          <p className="text-muted-foreground">
            This Cookie Policy explains how UniSchedule uses browser storage technologies to provide and improve our services. We believe in transparency about the data we store on your device.
          </p>
        </section>

        {/* What We Use */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Info className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">What We Use</h2>
          </div>
          
          <div className="space-y-4 pl-7">
            <p className="text-muted-foreground">
              UniSchedule primarily uses <strong>localStorage</strong> instead of traditional cookies. This is a browser storage mechanism that keeps data on your device.
            </p>
          </div>
        </section>

        {/* What We Store */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">What We Store</h2>
          </div>
          
          <div className="space-y-4 pl-7">
            <div className="bg-card border rounded-lg p-4 space-y-3">
              <h3 className="font-semibold">Essential Storage</h3>
              <ul className="list-disc list-inside space-y-2 text-muted-foreground text-sm">
                <li>
                  <strong>User Preferences</strong> - Your selected program, department, semester, and group
                </li>
                <li>
                  <strong>Theme Settings</strong> - Your light/dark mode preference
                </li>
                <li>
                  <strong>Notification Preferences</strong> - Whether you've enabled notifications
                </li>
                <li>
                  <strong>Read Status</strong> - Which notifications you've already seen
                </li>
              </ul>
            </div>

            <p className="text-sm text-muted-foreground">
              We do not use tracking cookies or third-party advertising cookies.
            </p>
          </div>
        </section>

        {/* Why We Use Storage */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Why We Use Local Storage</h2>
          
          <div className="space-y-3 text-muted-foreground pl-7">
            <p>We use localStorage to:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Remember your timetable preferences between visits</li>
              <li>Maintain your theme preference (light/dark mode)</li>
              <li>Provide a personalized experience without requiring an account</li>
              <li>Keep your data private on your device</li>
              <li>Improve app performance by reducing server requests</li>
            </ul>
          </div>
        </section>

        {/* Third-Party Services */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Third-Party Services</h2>
          
          <div className="space-y-3 text-muted-foreground pl-7">
            <p>We use minimal third-party services that may set their own cookies:</p>
            
            <div className="space-y-3 mt-3">
              <div className="bg-card border rounded-lg p-3">
                <h3 className="font-semibold text-foreground mb-1">Google Fonts</h3>
                <p className="text-sm">
                  Used for typography. May set cookies for font delivery optimization.
                </p>
              </div>
            </div>

            <p className="text-sm mt-3">
              These services are governed by their own privacy policies and cookie policies.
            </p>
          </div>
        </section>

        {/* Managing Storage */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold">Managing Your Data</h2>
          </div>
          
          <div className="space-y-4 pl-7">
            <p className="text-muted-foreground">
              You have full control over the data stored on your device:
            </p>

            <div className="bg-card border rounded-lg p-4 space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Clear All Data</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  To clear all UniSchedule data from your browser:
                </p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Open your browser's Developer Tools (F12)</li>
                  <li>Go to the "Application" or "Storage" tab</li>
                  <li>Find "Local Storage" in the sidebar</li>
                  <li>Select the UniSchedule domain</li>
                  <li>Click "Clear All" or delete individual items</li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Browser Settings</h3>
                <p className="text-sm text-muted-foreground">
                  You can also clear storage through your browser settings:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground mt-2">
                  <li><strong>Chrome:</strong> Settings → Privacy and security → Clear browsing data</li>
                  <li><strong>Firefox:</strong> Settings → Privacy & Security → Cookies and Site Data</li>
                  <li><strong>Safari:</strong> Preferences → Privacy → Manage Website Data</li>
                  <li><strong>Edge:</strong> Settings → Privacy → Clear browsing data</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Impact of Clearing Data */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Impact of Clearing Data</h2>
          
          <div className="bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 pl-7">
            <p className="text-sm text-yellow-900 dark:text-yellow-100">
              If you clear your browser storage, you will need to re-enter your preferences (program, department, semester, group) the next time you visit UniSchedule.
            </p>
          </div>
        </section>

        {/* Updates to Policy */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Updates to This Policy</h2>
          <p className="text-muted-foreground pl-7">
            We may update this Cookie Policy from time to time. Any changes will be reflected by updating the "Last updated" date at the top of this page.
          </p>
        </section>

        {/* Contact */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Questions?</h2>
          <p className="text-muted-foreground pl-7">
            If you have questions about our use of cookies or local storage, please contact us at:{' '}
            <a href="mailto:faisalh5556@gmail.com" className="text-primary hover:underline">
              faisalh5556@gmail.com
            </a>
          </p>
        </section>

        {/* Back Button */}
        <div className="pt-8 border-t">
          <Button onClick={() => navigate('/')} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
}
